import {
  type AccountCommitment,
  type DataService,
  type Hash,
  type PoolEventsError,
  type PoolInfo,
  type Secret,
  AccountService,
} from '@michael-leverup/privacy-pools-core-sdk';
import { aspClient } from '@ui/services/privacyPools/aspClient';
import { PRIVACY_POOLS_ASP_ENDPOINT } from '@ui/services/privacyPools/config';

import { poseidon } from './poseidon';
import {
  loadTerminalCheckpoint,
  saveTerminalCheckpoint,
  TERMINAL_CHECKPOINT_ACCOUNT_MARKER,
} from './terminalCheckpoint';
import type { ReconstructionMetadataByChainScope } from './types';
import { Hex } from 'viem';

const CANDIDATE_BATCH_SIZE = 128n;
const WITHDRAWAL_CANDIDATE_BATCH_SIZE = 128n;
const MAX_CONSECUTIVE_MISSES = 10;
const POOL_RECONSTRUCTION_CONCURRENCY = 4;

type ReconstructionSyncMeta = {
  indexedToBlock: bigint;
  chainHeadBlock: bigint;
  usedRpcTail: boolean;
  tailGapTooLarge: boolean;
};

type ReconstructionDepositRecord =
  ReconstructionMetadataByChainScope[string]['deposits'][number];
type ReconstructionWithdrawalRecord =
  ReconstructionMetadataByChainScope[string]['withdrawals'][number];
type ReconstructionRagequitRecord =
  ReconstructionMetadataByChainScope[string]['ragequits'][number];

type LoadAccountWithIndexedReconstructionResult = {
  accountService: AccountService;
  legacyAccountService: null;
  errors: PoolEventsError[];
  syncMetaByScope: Map<Hash, ReconstructionSyncMeta>;
  reconstructionMetadataByChainScope: ReconstructionMetadataByChainScope;
};

const toBigInt = (value: string): bigint => BigInt(value);

const toCommitmentTimestamp = (timestamp: number): bigint => {
  if (!Number.isFinite(timestamp) || timestamp <= 0) return 0n;
  const normalized =
    timestamp > 1_000_000_000_000
      ? Math.floor(timestamp / 1000)
      : Math.floor(timestamp);

  return BigInt(normalized);
};

const genWithdrawalNullifier = (
  accountService: AccountService,
  label: Hash,
  index: bigint,
): Secret => {
  const [masterNullifier] = accountService.account.masterKeys;

  return poseidon([masterNullifier, label, index]) as Secret;
};

const genWithdrawalSecret = (
  accountService: AccountService,
  label: Hash,
  index: bigint,
): Secret => {
  const [, masterSecret] = accountService.account.masterKeys;

  return poseidon([masterSecret, label, index]) as Secret;
};

const hashPrecommitment = (nullifier: Secret, secret: Secret): Hash => {
  return poseidon([nullifier, secret]) as Hash;
};

const hashCommitment = (
  value: bigint,
  label: Hash,
  precommitment: Hash,
): Hash => {
  return poseidon([value, label, precommitment]) as Hash;
};

const mergeReconstructionEvents = <
  T extends { txHash: Hex; logIndex: number; blockNumber: bigint },
>(
  primary: T[],
  secondary: T[],
): T[] => {
  const byId = new Map<string, T>();

  for (const event of [...primary, ...secondary]) {
    const id = `${event.txHash.toLowerCase()}:${event.logIndex}`;
    if (!byId.has(id)) {
      byId.set(id, event);
    }
  }

  return [...byId.values()].sort((left, right) => {
    if (left.blockNumber === right.blockNumber) {
      return left.logIndex - right.logIndex;
    }

    return left.blockNumber < right.blockNumber ? -1 : 1;
  });
};

const lookupDepositsFromIndex = async (
  pool: PoolInfo,
  candidates: Hash[],
): Promise<{
  indexedToBlock: bigint;
  chainHeadBlock: bigint;
  deposits: ReconstructionDepositRecord[];
}> => {
  const response = await aspClient.fetchAccountReconstructionDeposits(
    PRIVACY_POOLS_ASP_ENDPOINT,
    pool.chainId,
    pool.scope.toString(),
    {
      candidates: candidates.map((candidate) => candidate.toString()),
    },
  );

  return {
    indexedToBlock: toBigInt(response.indexedToBlock),
    chainHeadBlock: toBigInt(response.chainHeadBlock),
    deposits: response.deposits.map((deposit) => ({
      eventType: 'deposit',
      precommitmentHash: toBigInt(deposit.precommitmentHash) as Hash,
      commitment: toBigInt(deposit.commitment) as Hash,
      label: toBigInt(deposit.label) as Hash,
      amount: toBigInt(deposit.amount),
      address: deposit.address,
      txHash: deposit.txHash as Hex,
      logIndex: deposit.logIndex,
      blockNumber: toBigInt(deposit.blockNumber),
      timestamp: deposit.timestamp,
      reviewStatus: deposit.reviewStatus,
    })),
  };
};

const lookupWithdrawalsFromIndex = async (
  pool: PoolInfo,
  spentNullifiers: Hash[],
): Promise<{
  indexedToBlock: bigint;
  chainHeadBlock: bigint;
  withdrawals: ReconstructionWithdrawalRecord[];
}> => {
  const response = await aspClient.fetchAccountReconstructionWithdrawals(
    PRIVACY_POOLS_ASP_ENDPOINT,
    pool.chainId,
    pool.scope.toString(),
    {
      spentNullifiers: spentNullifiers.map((value) => value.toString()),
    },
  );

  return {
    indexedToBlock: toBigInt(response.indexedToBlock),
    chainHeadBlock: toBigInt(response.chainHeadBlock),
    withdrawals: response.withdrawals.map((withdrawal) => ({
      eventType: 'withdrawal',
      spentNullifier: toBigInt(withdrawal.spentNullifier) as Hash,
      newCommitment: toBigInt(withdrawal.newCommitment) as Hash,
      amount: toBigInt(withdrawal.amount),
      txHash: withdrawal.txHash as Hex,
      logIndex: withdrawal.logIndex,
      blockNumber: toBigInt(withdrawal.blockNumber),
      timestamp: withdrawal.timestamp,
    })),
  };
};

const reconstructPool = async (
  accountService: AccountService,
  pool: PoolInfo,
  seed: string,
): Promise<{
  syncMeta: ReconstructionSyncMeta;
  metadata: {
    deposits: ReconstructionDepositRecord[];
    withdrawals: ReconstructionWithdrawalRecord[];
    ragequits: ReconstructionRagequitRecord[];
  };
}> => {
  const syncMeta: ReconstructionSyncMeta = {
    indexedToBlock: 0n,
    chainHeadBlock: 0n,
    usedRpcTail: false,
    tailGapTooLarge: false,
  };

  const depositsById = new Map<string, ReconstructionDepositRecord>();
  const withdrawalsById = new Map<string, ReconstructionWithdrawalRecord>();

  const seenPrecommitments = new Set<string>();
  let index = 0n;
  let consecutiveMisses = 0;

  while (consecutiveMisses < MAX_CONSECUTIVE_MISSES) {
    const candidates: Array<{
      nullifier: Secret;
      secret: Secret;
      precommitment: Hash;
    }> = [];

    for (let offset = 0n; offset < CANDIDATE_BATCH_SIZE; offset++) {
      const candidateIndex = index + offset;
      const { nullifier, secret, precommitment } =
        accountService.createDepositSecrets(pool.scope, candidateIndex);
      candidates.push({ nullifier, secret, precommitment });
    }

    const candidatePrecommitments = candidates.map(
      (candidate) => candidate.precommitment,
    );
    const indexedDeposits = await lookupDepositsFromIndex(
      pool,
      candidatePrecommitments,
    );
    syncMeta.indexedToBlock = indexedDeposits.indexedToBlock;
    syncMeta.chainHeadBlock = indexedDeposits.chainHeadBlock;

    const mergedDeposits = mergeReconstructionEvents(
      indexedDeposits.deposits,
      [],
    );
    const byPrecommitment = new Map<string, ReconstructionDepositRecord>();
    for (const deposit of mergedDeposits) {
      const key = deposit.precommitmentHash.toString();
      if (!byPrecommitment.has(key)) {
        byPrecommitment.set(key, deposit);
      }
    }

    for (const candidate of candidates) {
      index += 1n;
      const event = byPrecommitment.get(candidate.precommitment.toString());

      if (
        !event ||
        seenPrecommitments.has(candidate.precommitment.toString())
      ) {
        consecutiveMisses += 1;
      } else {
        const poolAccount = accountService.addPoolAccount(
          pool.scope,
          event.amount,
          candidate.nullifier,
          candidate.secret,
          event.label,
          event.blockNumber,
          event.txHash,
        );

        poolAccount.deposit.timestamp = toCommitmentTimestamp(event.timestamp);
        seenPrecommitments.add(candidate.precommitment.toString());
        depositsById.set(
          `${event.txHash.toLowerCase()}:${event.logIndex}`,
          event,
        );
        consecutiveMisses = 0;
      }

      if (consecutiveMisses >= MAX_CONSECUTIVE_MISSES) {
        break;
      }
    }
  }

  const accounts = accountService.account.poolAccounts.get(pool.scope) ?? [];
  const terminalSet = await loadTerminalCheckpoint({
    seed,
    chainId: pool.chainId,
    scope: pool.scope,
  });
  const nextTerminalSet = new Set(terminalSet);

  const markTerminalCheckpoint = (poolAccount: object) => {
    Reflect.set(poolAccount, TERMINAL_CHECKPOINT_ACCOUNT_MARKER, true);
  };

  for (const poolAccount of accounts) {
    const labelKey = poolAccount.label.toString();

    if (poolAccount.ragequit) {
      nextTerminalSet.add(labelKey);
      markTerminalCheckpoint(poolAccount);
      continue;
    }

    if (terminalSet.has(labelKey)) {
      markTerminalCheckpoint(poolAccount);
      continue;
    }

    let currentCommitment: AccountCommitment =
      poolAccount.children.length > 0
        ? poolAccount.children[poolAccount.children.length - 1]!
        : poolAccount.deposit;

    if (currentCommitment.value === 0n) {
      nextTerminalSet.add(labelKey);
      markTerminalCheckpoint(poolAccount);
      continue;
    }

    let withdrawalIndex = BigInt(poolAccount.children.length);

    while (true) {
      const batchStartIndex = withdrawalIndex;
      const candidateSpentNullifiers: Hash[] = [
        poseidon([currentCommitment.nullifier]) as Hash,
      ];

      for (
        let offset = 1n;
        offset < WITHDRAWAL_CANDIDATE_BATCH_SIZE;
        offset++
      ) {
        const candidateIndex = batchStartIndex + offset - 1n;
        const candidateNullifier = genWithdrawalNullifier(
          accountService,
          poolAccount.label,
          candidateIndex,
        );
        candidateSpentNullifiers.push(poseidon([candidateNullifier]) as Hash);
      }

      const indexedWithdrawals = await lookupWithdrawalsFromIndex(
        pool,
        candidateSpentNullifiers,
      );
      syncMeta.indexedToBlock = indexedWithdrawals.indexedToBlock;
      syncMeta.chainHeadBlock = indexedWithdrawals.chainHeadBlock;

      const mergedWithdrawals = mergeReconstructionEvents(
        indexedWithdrawals.withdrawals,
        [],
      );
      for (const withdrawal of mergedWithdrawals) {
        withdrawalsById.set(
          `${withdrawal.txHash.toLowerCase()}:${withdrawal.logIndex}`,
          withdrawal,
        );
      }

      const withdrawalsBySpentNullifier = new Map<
        string,
        ReconstructionWithdrawalRecord
      >();
      for (const withdrawal of mergedWithdrawals) {
        const key = withdrawal.spentNullifier.toString();
        if (!withdrawalsBySpentNullifier.has(key)) {
          withdrawalsBySpentNullifier.set(key, withdrawal);
        }
      }

      let shouldStop = false;

      for (
        let offset = 0n;
        offset < WITHDRAWAL_CANDIDATE_BATCH_SIZE;
        offset++
      ) {
        const candidateIndex = batchStartIndex + offset;
        const candidateSpentNullifier =
          candidateSpentNullifiers[Number(offset)]!;
        const matchedWithdrawal = withdrawalsBySpentNullifier.get(
          candidateSpentNullifier.toString(),
        );

        if (!matchedWithdrawal) {
          shouldStop = true;
          break;
        }

        if (matchedWithdrawal.spentNullifier !== candidateSpentNullifier) {
          throw new Error(
            'Account reconstruction failed: spent nullifier mismatch in indexed response',
          );
        }

        if (matchedWithdrawal.amount > currentCommitment.value) {
          throw new Error(
            'Account reconstruction failed: withdrawal amount exceeds commitment value',
          );
        }

        const nullifier = genWithdrawalNullifier(
          accountService,
          poolAccount.label,
          candidateIndex,
        );
        const secret = genWithdrawalSecret(
          accountService,
          poolAccount.label,
          candidateIndex,
        );
        const remainingValue =
          currentCommitment.value - matchedWithdrawal.amount;
        const computedNewCommitment = hashCommitment(
          remainingValue,
          poolAccount.label,
          hashPrecommitment(nullifier, secret),
        );

        if (computedNewCommitment !== matchedWithdrawal.newCommitment) {
          throw new Error(
            'Account reconstruction failed: withdrawal newCommitment mismatch',
          );
        }

        currentCommitment = accountService.addWithdrawalCommitment(
          currentCommitment,
          remainingValue,
          nullifier,
          secret,
          matchedWithdrawal.blockNumber,
          matchedWithdrawal.txHash,
        );
        currentCommitment.timestamp = toCommitmentTimestamp(
          matchedWithdrawal.timestamp,
        );
        withdrawalIndex = candidateIndex + 1n;

        if (remainingValue === 0n) {
          nextTerminalSet.add(labelKey);
          markTerminalCheckpoint(poolAccount);
          shouldStop = true;
          break;
        }
      }

      if (shouldStop) {
        break;
      }
    }
  }

  await saveTerminalCheckpoint({
    seed,
    chainId: pool.chainId,
    scope: pool.scope,
    terminalSet: nextTerminalSet,
  });

  return {
    syncMeta,
    metadata: {
      deposits: [...depositsById.values()].sort((a, b) => {
        if (a.blockNumber === b.blockNumber) return a.logIndex - b.logIndex;

        return a.blockNumber < b.blockNumber ? -1 : 1;
      }),
      withdrawals: [...withdrawalsById.values()].sort((a, b) => {
        if (a.blockNumber === b.blockNumber) return a.logIndex - b.logIndex;

        return a.blockNumber < b.blockNumber ? -1 : 1;
      }),
      ragequits: [],
    },
  };
};

export const loadAccountWithIndexedReconstruction = async (
  dataService: DataService,
  seed: string,
  pools: PoolInfo[],
): Promise<LoadAccountWithIndexedReconstructionResult> => {
  const accountService = new AccountService(dataService, { mnemonic: seed });
  const errors: PoolEventsError[] = [];
  const syncMetaByScope = new Map<Hash, ReconstructionSyncMeta>();
  const reconstructionMetadataByChainScope: ReconstructionMetadataByChainScope =
    {};
  const clearScopeState = (pool: PoolInfo) => {
    accountService.account.poolAccounts.delete(pool.scope);
    syncMetaByScope.delete(pool.scope);
    delete reconstructionMetadataByChainScope[`${pool.chainId}-${pool.scope}`];
  };

  const queue = [...pools];
  const workers = Array.from(
    {
      length: Math.min(POOL_RECONSTRUCTION_CONCURRENCY, pools.length),
    },
    async () => {
      while (queue.length > 0) {
        const pool = queue.shift();
        if (!pool) break;

        try {
          const result = await reconstructPool(accountService, pool, seed);
          syncMetaByScope.set(pool.scope, result.syncMeta);
          reconstructionMetadataByChainScope[`${pool.chainId}-${pool.scope}`] =
            result.metadata;
        } catch (error) {
          clearScopeState(pool);
          errors.push({
            scope: pool.scope,
            reason: error instanceof Error ? error.message : String(error),
          });
        }
      }
    },
  );

  await Promise.all(workers);

  return {
    accountService,
    legacyAccountService: null,
    errors,
    syncMetaByScope,
    reconstructionMetadataByChainScope,
  };
};

export type {
  LoadAccountWithIndexedReconstructionResult,
  ReconstructionSyncMeta,
};
